import React, { useState, useEffect, useMemo } from 'react';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import StoreInSetup from '../common/StoreInSetup';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import { useWebStore } from '../../context/WebStoreContext';
import { getFontFamilyCss, getTitleFontSizeCss, getSubtitleFontSizeCss } from '../../utils/fontHelper';

export const DEFAULT_FAQ_SECTION = {
  enabled: true,
  showInNavbar: false,
  badgeText: 'Centro de Ayuda',
  title: 'Preguntas Frecuentes',
  subtitle: 'Respondemos tus dudas sobre compras mayoristas y minoristas, envíos, medios de pago y retiros.',
  showSearch: true,
  showCategories: true,
  showWhatsappCta: true,
  whatsappCtaTitle: '¿No encontraste la respuesta que buscabas?',
  whatsappCtaSubtitle: 'Nuestro equipo comercial está disponible en WhatsApp para asesorarte al instante.',
  whatsappCtaButtonText: 'Consultar por WhatsApp',
  backgroundColor: '#f8fafc',
  items: [
    {
      id: 'faq-1',
      category: 'Compras y Precios',
      question: '¿Realizan ventas por mayor y por menor?',
      answer: 'Sí, realizamos ventas tanto minoristas como mayoristas y a distribuidores. Contamos con escalas de precios especiales por bulto cerrado y por volumen de compra.',
      is_active: true
    },
    {
      id: 'faq-2',
      category: 'Envíos y Entregas',
      question: '¿Hacen envíos a todo el país? ¿Cómo se despachan los pedidos?',
      answer: 'Sí, enviamos a toda la Argentina. Realizamos entregas con logística propia en zonas de cobertura y despachamos al interior del país a través del expreso o transporte de tu preferencia.',
      is_active: true
    },
    {
      id: 'faq-3',
      category: 'Medios de Pago',
      question: '¿Cuáles son los medios de pago disponibles?',
      answer: 'Aceptamos transferencia bancaria, depósito, efectivo al retirar en planta, Mercado Pago y medios electrónicos habilitados. Emitimos Factura A y B según tu condición fiscal.',
      is_active: true
    },
    {
      id: 'faq-4',
      category: 'Compras y Precios',
      question: '¿Cómo puedo ver mi lista de precios mayorista en la tienda online?',
      answer: 'Podés registrarte gratis desde el botón "Registrarme" en la parte superior del sitio. Una vez que nuestro equipo comercial asigne tu lista mayorista o distribuidor, verás tus precios personalizados automáticamente al iniciar sesión.',
      is_active: true
    },
    {
      id: 'faq-5',
      category: 'Retiros y Horarios',
      question: '¿Puedo retirar mi pedido personalmente por fábrica o depósito?',
      answer: '¡Por supuesto! Una vez confirmado y preparado tu pedido, podés retirarlo sin cargo por nuestras instalaciones dentro de nuestro horario de atención comercial.',
      is_active: true
    }
  ]
};

export const FaqSectionBlock = ({
  faqConfig = DEFAULT_FAQ_SECTION,
  whatsappNumber = '',
  padClass = 'py-5',
  secFontStyle = {},
  globalTitleFontSize,
  globalSubtitleFontSize
}) => {
  const mergedConfig = useMemo(() => ({
    ...DEFAULT_FAQ_SECTION,
    ...(faqConfig || {}),
    items: Array.isArray(faqConfig?.items) ? faqConfig.items : DEFAULT_FAQ_SECTION.items
  }), [faqConfig]);

  const activeItems = useMemo(
    () => (mergedConfig.items || []).filter(item => item && item.is_active !== false && String(item.question || '').trim() !== ''),
    [mergedConfig.items]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [openIds, setOpenIds] = useState(() => (activeItems[0]?.id ? [activeItems[0].id] : []));

  const categories = useMemo(() => {
    const set = new Set();
    activeItems.forEach(item => {
      const cat = String(item.category || '').trim();
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, [activeItems]);

  useEffect(() => {
    if (selectedCategory !== 'Todas' && !categories.includes(selectedCategory)) {
      setSelectedCategory('Todas');
    }
  }, [categories, selectedCategory]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return activeItems.filter(item => {
      if (selectedCategory !== 'Todas' && String(item.category || '').trim() !== selectedCategory) {
        return false;
      }
      if (!q) return true;
      const qText = String(item.question || '').toLowerCase();
      const aText = String(item.answer || '').toLowerCase();
      const cText = String(item.category || '').toLowerCase();
      return qText.includes(q) || aText.includes(q) || cText.includes(q);
    });
  }, [activeItems, selectedCategory, searchQuery]);

  const toggleItem = (id) => {
    setOpenIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const bg = mergedConfig.backgroundColor || 'var(--ecom-bg, #f8fafc)';
  const cleanWa = String(whatsappNumber || '').replace(/\D/g, '');

  return (
    <section
      className={`${padClass} border-top border-bottom`}
      style={{
        backgroundColor: bg,
        borderColor: 'var(--ecom-border, #e2e8f0)',
        ...secFontStyle
      }}
    >
      <div className="container" style={{ maxWidth: '920px' }}>
        {/* Encabezado */}
        <div className="text-center mb-4 pb-1">
          {mergedConfig.badgeText && (
            <span
              className="badge fw-bold px-3 py-2 rounded-pill text-uppercase mb-2"
              style={{
                backgroundColor: 'var(--ecom-primary-light, rgba(13, 110, 253, 0.12))',
                color: 'var(--ecom-primary, #0d6efd)'
              }}
            >
              <i className="bi bi-patch-question-fill me-2"></i>
              {mergedConfig.badgeText}
            </span>
          )}
          <h2
            className="fw-bold mb-2"
            style={{
              color: 'var(--ecom-text, #1e293b)',
              fontSize: getTitleFontSizeCss(mergedConfig.titleFontSize || globalTitleFontSize)
            }}
          >
            {mergedConfig.title || 'Preguntas Frecuentes'}
          </h2>
          {mergedConfig.subtitle && (
            <p
              className="text-muted mx-auto mb-0"
              style={{
                maxWidth: '680px',
                fontSize: getSubtitleFontSizeCss(mergedConfig.subtitleFontSize || globalSubtitleFontSize)
              }}
            >
              {mergedConfig.subtitle}
            </p>
          )}
        </div>

        {/* Buscador y Filtros por Categoría */}
        {(mergedConfig.showSearch !== false || (mergedConfig.showCategories !== false && categories.length > 1)) && (
          <div className="mb-4">
            {mergedConfig.showSearch !== false && (
              <div className="position-relative mx-auto mb-3" style={{ maxWidth: '540px' }}>
                <i
                  className="bi bi-search position-absolute top-50 translate-middle-y text-primary"
                  style={{ left: '16px', fontSize: '1rem' }}
                ></i>
                <input
                  type="text"
                  className="form-control rounded-pill shadow-xs border py-2"
                  style={{
                    paddingLeft: '44px',
                    paddingRight: searchQuery ? '42px' : '18px',
                    backgroundColor: 'var(--ecom-card-bg, #ffffff)',
                    borderColor: 'var(--ecom-border, #e2e8f0)',
                    color: 'var(--ecom-text, #1e293b)'
                  }}
                  placeholder="Buscar una duda o palabra clave (ej: envíos, mayorista, factura)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn border-0 bg-transparent position-absolute top-50 translate-middle-y text-muted p-0"
                    style={{ right: '14px' }}
                    onClick={() => setSearchQuery('')}
                    title="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-circle-fill"></i>
                  </button>
                )}
              </div>
            )}

            {mergedConfig.showCategories !== false && categories.length > 1 && (
              <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
                {['Todas', ...categories].map(cat => {
                  const isActive = selectedCategory === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold transition-all ${
                        isActive ? 'shadow-xs' : 'bg-white border text-secondary'
                      }`}
                      style={
                        isActive
                          ? {
                              backgroundColor: 'var(--ecom-primary, #0d6efd)',
                              color: 'var(--ecom-primary-text, #ffffff)',
                              border: '1px solid var(--ecom-primary, #0d6efd)'
                            }
                          : {}
                      }
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Lista de Acordeón de Preguntas */}
        {filteredItems.length === 0 ? (
          <div
            className="text-center p-5 rounded-4 border bg-white shadow-xs"
            style={{ borderColor: 'var(--ecom-border, #e2e8f0)' }}
          >
            <i className="bi bi-question-circle fs-1 text-muted opacity-50 d-block mb-2"></i>
            <h6 className="fw-bold text-dark mb-1">No encontramos preguntas que coincidan con tu búsqueda</h6>
            <p className="text-muted small mb-3">Probá con otras palabras clave o consultanos directamente por WhatsApp.</p>
            {searchQuery && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-semibold"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('Todas');
                }}
              >
                Ver todas las preguntas
              </button>
            )}
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {filteredItems.map((item, idx) => {
              const itemId = item.id || `faq-${idx}`;
              const isOpen = openIds.includes(itemId);
              return (
                <div
                  key={itemId}
                  className="rounded-4 border overflow-hidden transition-all"
                  style={{
                    backgroundColor: 'var(--ecom-card-bg, #ffffff)',
                    borderColor: isOpen ? 'var(--ecom-primary, #0d6efd)' : 'var(--ecom-border, #e2e8f0)',
                    boxShadow: isOpen ? '0 6px 20px rgba(15, 23, 42, 0.07)' : '0 1px 3px rgba(15, 23, 42, 0.03)'
                  }}
                >
                  <button
                    type="button"
                    className="w-100 text-start border-0 bg-transparent p-4 p-md-4 d-flex align-items-center justify-content-between gap-3"
                    onClick={() => toggleItem(itemId)}
                    aria-expanded={isOpen}
                  >
                    <div className="d-flex align-items-start align-items-md-center gap-3 min-w-0">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                        style={{
                          width: '34px',
                          height: '34px',
                          backgroundColor: isOpen
                            ? 'var(--ecom-primary, #0d6efd)'
                            : 'var(--ecom-primary-light, rgba(13, 110, 253, 0.12))',
                          color: isOpen
                            ? 'var(--ecom-primary-text, #ffffff)'
                            : 'var(--ecom-primary, #0d6efd)',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <i className="bi bi-question-lg"></i>
                      </div>
                      <div className="min-w-0">
                        {item.category && (
                          <span
                            className="badge rounded-pill mb-1 fw-semibold"
                            style={{
                              fontSize: '0.68rem',
                              backgroundColor: 'rgba(100, 116, 139, 0.12)',
                              color: '#475569'
                            }}
                          >
                            {item.category}
                          </span>
                        )}
                        <h5
                          className="fw-bold mb-0"
                          style={{
                            fontSize: '1.02rem',
                            color: isOpen ? 'var(--ecom-primary, #0d6efd)' : 'var(--ecom-text, #1e293b)',
                            lineHeight: 1.35
                          }}
                        >
                          {item.question}
                        </h5>
                      </div>
                    </div>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 border"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: isOpen ? 'var(--ecom-primary-light, rgba(13,110,253,0.1))' : '#f8fafc',
                        color: isOpen ? 'var(--ecom-primary, #0d6efd)' : '#64748b',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} fw-bold`}></i>
                    </div>
                  </button>

                  {isOpen && (
                    <div
                      className="px-3 px-md-4 pb-4 pt-0 border-top"
                      style={{
                        borderColor: 'rgba(226, 232, 240, 0.6)'
                      }}
                    >
                      <div
                        className="pt-3 text-secondary lh-lg"
                        style={{
                          fontSize: '0.95rem',
                          whiteSpace: 'pre-line',
                          paddingLeft: '46px'
                        }}
                      >
                        {item.answer}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bloque CTA de WhatsApp al pie de las Preguntas Frecuentes */}
        {mergedConfig.showWhatsappCta !== false && cleanWa && (
          <div
            className="mt-4 p-4 rounded-4 border d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 text-center text-md-start shadow-xs"
            style={{
              backgroundColor: 'var(--ecom-card-bg, #ffffff)',
              borderColor: 'var(--ecom-border, #e2e8f0)'
            }}
          >
            <div className="d-flex flex-column flex-md-row align-items-center gap-3">
              <div
                className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}
              >
                <i className="bi bi-whatsapp"></i>
              </div>
              <div>
                <h6 className="fw-bold mb-1" style={{ color: 'var(--ecom-text, #1e293b)', fontSize: '1.02rem' }}>
                  {mergedConfig.whatsappCtaTitle || '¿No encontraste la respuesta que buscabas?'}
                </h6>
                <p className="text-muted small mb-0">
                  {mergedConfig.whatsappCtaSubtitle || 'Nuestro equipo comercial está disponible en WhatsApp para asesorarte al instante.'}
                </p>
              </div>
            </div>
            <a
              href={`https://wa.me/${cleanWa}?text=${encodeURIComponent('¡Hola! Estuve viendo la sección de Preguntas Frecuentes y quería hacerles una consulta.')}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-success rounded-pill px-4 py-2 fw-bold text-white shadow-xs d-inline-flex align-items-center gap-2 flex-shrink-0"
            >
              <i className="bi bi-whatsapp"></i>
              <span>{mergedConfig.whatsappCtaButtonText || 'Consultar por WhatsApp'}</span>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

const FaqPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig: globalLandingConfig, rubros, ecomCustomer: contextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(globalLandingConfig || null);

  useEffect(() => {
    if (globalLandingConfig) {
      setConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);

  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    localStorage.setItem('sigecom_ecom_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const totalCartCount = cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  const totalCartPrice = cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const isInSetup = Boolean(
    storeConfig?.is_in_setup ||
    config?.is_in_setup ||
    storeConfig?.status === 'en_habilitacion' ||
    config?.status === 'en_habilitacion' ||
    storeConfig?.status === 'no_encontrada' ||
    config?.status === 'no_encontrada'
  );

  if (isInSetup) {
    return (
      <StoreInSetup
        storeConfig={{
          ...config,
          ...storeConfig,
          company_name: storeConfig?.company_name || config?.company_name
        }}
      />
    );
  }

  const whatsappNumber =
    config?.contactSection?.whatsapp ||
    config?.topBar?.whatsapp ||
    storeConfig?.footer?.whatsapp ||
    config?.whatsappFloating?.phone ||
    '';

  return (
    <div
      className="webstore-site-wrapper min-vh-100 d-flex flex-column"
      style={{
        fontFamily: getFontFamilyCss(config?.fontFamily || config?.colors?.fontFamily) || 'Inter, system-ui, -apple-system, sans-serif',
        backgroundColor: 'var(--ecom-bg, #f8fafc)',
        color: 'var(--ecom-text, #1e293b)'
      }}
    >
      <UnifiedHeader
        activePage="faq"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      <main className="flex-grow-1">
        <FaqSectionBlock
          faqConfig={config?.faqSection || DEFAULT_FAQ_SECTION}
          whatsappNumber={whatsappNumber}
          padClass="py-5"
          globalTitleFontSize={config?.titleFontSize}
          globalSubtitleFontSize={config?.subtitleFontSize}
        />
      </main>

      <UnifiedFooter
        storeConfig={storeConfig}
        landingConfig={config}
        rubros={rubros}
      />

      <EcommerceCartDrawer
        show={isCartOpen}
        onHide={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={(cartKey, newQty) => {
          if (newQty <= 0) {
            setCartItems(prev => prev.filter(i => i.cartKey !== cartKey));
          } else {
            setCartItems(prev => prev.map(i => (i.cartKey === cartKey ? { ...i, quantity: newQty } : i)));
          }
        }}
        onRemoveItem={(cartKey) => setCartItems(prev => prev.filter(i => i.cartKey !== cartKey))}
        onClearCart={() => setCartItems([])}
        onProceedToCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
      />

      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        customer={activeCustomer}
        onOrderSuccess={() => {
          setCartItems([]);
          setIsCheckoutOpen(false);
        }}
        whatsappNumber={whatsappNumber}
      />

      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onLoginSuccess={(customerData) => {
          setEcomCustomer(customerData);
          setIsAuthModalOpen(false);
        }}
      />

      <EcommerceWhatsAppFloating config={config?.whatsappFloating || storeConfig?.whatsappFloating} />
    </div>
  );
};

export default FaqPage;
